import React, { useEffect, useState } from 'react';
import { User, Mail, Lock, Building, MapPin, Save, Eye, EyeOff, ChevronDown, ChevronUp, Wallet, TrendingDown, Calendar } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';
import { supabaseClient } from '../lib/supabase';
import { getCompanies } from '../utils/companyManagement';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { useBalance, formatMonth } from '../contexts/BalanceContext';
export function EmployeeProfilePage() {
  useDocumentTitle('Mi Perfil');
  const {
    user
  } = useAuth();
  const {
    currentBalance,
    loading: balanceLoading,
    hasBalance
  } = useBalance();
  const {
    showToast
  } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [employeeData, setEmployeeData] = useState<any>(null);
  const [companyData, setCompanyData] = useState<any>(null);
  useEffect(() => {
    loadEmployeeData();
  }, [user]);
  const loadEmployeeData = async () => {
    if (!user?.employeeId) return;
    setLoading(true);
    try {
      const employees = await supabaseClient.getEmployees();
      const employee = employees.find((e: any) => e.id === user.employeeId);
      if (employee) {
        setEmployeeData(employee);
        setFormData({
          name: employee.name || '',
          email: employee.email || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        // Load company data to check subsidyEnabled
        try {
          const allCompanies = await supabaseClient.getCompanies();
          const empCompany = allCompanies.find((c: any) => c.id === employee.company_id);
          setCompanyData(empCompany || null);
        } catch (e) {
          // Fallback to localStorage
          const saved = localStorage.getItem('criket_companies');
          if (saved) {
            const allCompanies = JSON.parse(saved);
            const empCompany = allCompanies.find((c: any) => c.id === employee.company_id);
            setCompanyData(empCompany || null);
          }
        }
      }
    } catch (error) {
      console.error('Error loading employee data:', error);
      showToast('error', 'Error', 'No se pudo cargar la información');
    } finally {
      setLoading(false);
    }
  };
  const handleSave = async () => {
    // Validations
    if (!formData.name.trim()) {
      showToast('warning', 'Campo requerido', 'El nombre es obligatorio');
      return;
    }
    if (!formData.email.trim()) {
      showToast('warning', 'Campo requerido', 'El email es obligatorio');
      return;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast('warning', 'Email inválido', 'Por favor ingresa un email válido');
      return;
    }
    // Password change validation
    if (formData.newPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        showToast('warning', 'Contraseña actual requerida', 'Debes ingresar tu contraseña actual para cambiarla');
        return;
      }
      if (formData.currentPassword !== employeeData.password) {
        showToast('error', 'Contraseña incorrecta', 'La contraseña actual no es correcta');
        return;
      }
      if (formData.newPassword.length < 6) {
        showToast('warning', 'Contraseña muy corta', 'La nueva contraseña debe tener al menos 6 caracteres');
        return;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        showToast('error', 'Contraseñas no coinciden', 'La nueva contraseña y la confirmación deben ser iguales');
        return;
      }
    }
    setSaving(true);
    try {
      const updateData: any = {
        email: formData.email.trim()
      };
      // Only update password if new password is provided
      if (formData.newPassword) {
        updateData.password = formData.newPassword;
      }
      await supabaseClient.updateEmployee(user!.employeeId!, updateData);
      showToast('success', 'Perfil actualizado', 'Tu información ha sido actualizada correctamente');
      // Clear password fields
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      // Reload data
      await loadEmployeeData();
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('error', 'Error', 'No se pudo actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };
  const companies = getCompanies();
  const company = companyData || companies.find(c => c.id === employeeData?.company_id);
  const branch = company?.branches?.find((b: any) => b.id === employeeData?.branch_id);
  const subsidyEnabled = company?.subsidyEnabled === true;
  if (loading) {
    return <div className="flex items-center justify-center py-20" data-id="element-3470">
        <div className="text-center" data-id="element-3471">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black dark:border-white mx-auto mb-3" data-id="element-3472"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3473">
            Cargando perfil...
          </p>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-lg mx-auto space-y-4" data-id="element-3474">
      {/* Profile Card */}
      <Card noPadding data-id="element-3475">
        {/* Name + position header — no avatar */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800" data-id="element-3476">
          <p className="font-bold text-gray-900 dark:text-white text-xl leading-tight truncate" data-id="element-3477">
            {formData.name}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-3478">
            {employeeData?.position || 'Empleado'}
          </p>
        </div>

        {/* Company + Branch */}
        <div className="flex items-center gap-4 px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-400" data-id="element-3479">
          <span className="flex items-center gap-1.5" data-id="element-3480">
            <Building size={13} className="text-gray-400 shrink-0" data-id="element-3481" />
            <span className="font-medium text-gray-800 dark:text-gray-200" data-id="element-3482">
              {company?.name || 'N/A'}
            </span>
          </span>
          <span className="text-gray-300 dark:text-gray-700" data-id="element-3483">·</span>
          <span className="flex items-center gap-1.5" data-id="element-3484">
            <MapPin size={13} className="text-gray-400 shrink-0" data-id="element-3485" />
            <span className="font-medium text-gray-800 dark:text-gray-200" data-id="element-3486">
              {branch?.name || 'N/A'}
            </span>
          </span>
        </div>

        {/* Email field */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800" data-id="element-3487">
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2" data-id="element-3488">
            Email
          </label>
          <div className="relative" data-id="element-3489">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-3490" />
            <input type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="tu@email.com" data-id="element-3491" />
          </div>
        </div>

        {/* Password section — collapsible */}
        <div className="border-b border-gray-100 dark:border-gray-800" data-id="element-3492">
          <button onClick={() => setShowPasswordSection(!showPasswordSection)} className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-3493">
            <span className="flex items-center gap-2" data-id="element-3494">
              <Lock size={15} className="text-gray-400" data-id="element-3495" />
              Cambiar Contraseña
            </span>
            {showPasswordSection ? <ChevronUp size={16} className="text-gray-400" data-id="element-3496" /> : <ChevronDown size={16} className="text-gray-400" data-id="element-3497" />}
          </button>

          {showPasswordSection && <div className="px-5 pb-4 space-y-3" data-id="element-3498">
              <div className="relative" data-id="element-3499">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} data-id="element-3500" />
                <input type={showPassword ? 'text' : 'password'} value={formData.currentPassword} onChange={e => setFormData({
              ...formData,
              currentPassword: e.target.value
            })} className="w-full pl-9 pr-10 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="Contraseña actual" data-id="element-3501" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3502">
                  {showPassword ? <EyeOff size={15} data-id="element-3503" /> : <Eye size={15} data-id="element-3504" />}
                </button>
              </div>
              <div className="relative" data-id="element-3505">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} data-id="element-3506" />
                <input type={showNewPassword ? 'text' : 'password'} value={formData.newPassword} onChange={e => setFormData({
              ...formData,
              newPassword: e.target.value
            })} className="w-full pl-9 pr-10 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="Nueva contraseña (mín. 6 caracteres)" data-id="element-3507" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3508">
                  {showNewPassword ? <EyeOff size={15} data-id="element-3509" /> : <Eye size={15} data-id="element-3510" />}
                </button>
              </div>
              <div className="relative" data-id="element-3511">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} data-id="element-3512" />
                <input type={showNewPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={e => setFormData({
              ...formData,
              confirmPassword: e.target.value
            })} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="Confirmar nueva contraseña" data-id="element-3513" />
              </div>
            </div>}
        </div>

        {/* Save button */}
        <div className="px-5 py-4" data-id="element-3514">
          <Button onClick={handleSave} disabled={saving} leftIcon={saving ? undefined : <Save size={15} data-id="element-3516" />} fullWidth data-id="element-3515">
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </Card>

      {/* Balance Card — shown when company has subsidy enabled */}
      {subsidyEnabled && !balanceLoading && <Card noPadding data-id="element-3517">
          {/* Accent bar */}
          <div className="h-1 bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-400 rounded-t-xl" data-id="element-3518" />
          <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800" data-id="element-3519">
            <div className="flex items-center gap-2" data-id="element-3520">
              <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg" data-id="element-3521">
                <Wallet size={15} className="text-emerald-600 dark:text-emerald-400" data-id="element-3522" />
              </div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-3523">
                Mi Balance del Mes
              </h3>
            </div>
            {currentBalance && <span className="flex items-center gap-1 text-[11px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full font-semibold" data-id="element-3524">
                <Calendar size={10} data-id="element-3525" />
                {formatMonth(currentBalance.month, currentBalance.year)}
              </span>}
          </div>

          {currentBalance ? <>
              <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-gray-800" data-id="element-3526">
                <div className="px-4 py-4" data-id="element-3527">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5" data-id="element-3528">
                    Disponible
                  </p>
                  <p className={`text-sm font-bold font-mono tabular-nums whitespace-nowrap ${currentBalance.currentBalance > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-3529">
                    RD$ {currentBalance.currentBalance.toFixed(2)}
                  </p>
                  <div className="mt-2 w-full bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden" data-id="element-3530">
                    <div className="bg-emerald-500 h-full rounded-full transition-all duration-700" style={{
                width: `${Math.min(100, currentBalance.currentBalance / currentBalance.assignedAmount * 100)}%`
              }} data-id="element-3531" />
                  </div>
                </div>
                <div className="px-4 py-4" data-id="element-3532">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5" data-id="element-3533">
                    Asignado
                  </p>
                  <p className="text-sm font-bold font-mono tabular-nums whitespace-nowrap text-gray-900 dark:text-white" data-id="element-3534">
                    RD$ {currentBalance.assignedAmount.toFixed(2)}
                  </p>
                </div>
                <div className="px-4 py-4" data-id="element-3535">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5" data-id="element-3536">
                    Consumido
                  </p>
                  <p className="text-sm font-bold font-mono tabular-nums whitespace-nowrap text-orange-600 dark:text-orange-400" data-id="element-3537">
                    RD${' '}
                    {(currentBalance.assignedAmount - currentBalance.currentBalance).toFixed(2)}
                  </p>
                </div>
              </div>
              {currentBalance.notes && <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50" data-id="element-3538">
                  {currentBalance.notes}
                </div>}
            </> : <div className="px-5 py-6 text-center" data-id="element-3539">
              <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-3540">
                <Wallet size={18} className="text-gray-400" data-id="element-3541" />
              </div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3542">
                Sin balance asignado este mes
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5" data-id="element-3543">
                Contacta a tu gerente para solicitar tu subsidio
              </p>
            </div>}
        </Card>}
    </div>;
}